import React, { useMemo } from 'react';
import targets from './salesDataTargets.json';
import Link from './Link';
import { SEARCH_MAX_RESULTS, SEARCH_MIN_LENGTH } from './constants';
import { fmtDate, fmtMoney } from './format';
import { searchInvoices } from './salesCalculations';
import { amountStyle, cardStyle, headRowStyle, headStyle, headTextStyle, tableStyle, textCellStyle, stripeStyle } from './styles';

export const isSearching = (query) => query.trim().length >= SEARCH_MIN_LENGTH;

const searchBoxStyle = { position: 'relative', width: '100%', maxWidth: '50rem' };

const searchInputStyle = {
    width: '100%',
    height: '3rem',
    margin: 0,
    padding: '0 1rem 0 3rem',
    fontSize: '1rem',
    border: '1px solid #cacaca',
    borderRadius: '1.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    outline: 'none',
};

const searchIconStyle = {
    position: 'absolute',
    left: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    color: '#888',
};

const SearchIcon = () => (
    <svg style={searchIconStyle} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7" />
        <line x1="16.5" y1="16.5" x2="21" y2="21" />
    </svg>
);

const InvoiceSearch = ({ invoices, query, onQueryChange, onSelect }) => {
    const searching = isSearching(query);
    const matches = useMemo(() => (searching ? searchInvoices(invoices, query) : []), [invoices, query, searching]);
    const shown = matches.slice(0, SEARCH_MAX_RESULTS);

    return (
        <div style={{ marginBottom: '1.5rem' }}>
            <div style={searchBoxStyle}>
                <SearchIcon />
                <input
                    type="search"
                    value={query}
                    onChange={(e) => onQueryChange(e.target.value)}
                    placeholder={`Sök på fakturanummer eller kundnamn (endast fakturor från ${targets.year})`}
                    aria-label="Sök faktura"
                    style={searchInputStyle}
                />
            </div>

            {searching && (
                <div style={{ marginTop: '1rem' }}>
                    {matches.length === 0 && <div>Inga fakturor matchade sökningen.</div>}

                    {matches.length > 0 && (
                        <div style={cardStyle}>
                            <table style={tableStyle}>
                                <thead>
                                    <tr style={headRowStyle}>
                                        <th style={headTextStyle}>Fakturanr</th>
                                        <th style={headTextStyle}>Fakturadatum</th>
                                        <th style={headTextStyle}>Kund</th>
                                        <th style={headTextStyle}>Vår referens</th>
                                        <th style={headStyle}>Netto</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {shown.map((invoice, i) => (
                                        <tr key={invoice.documentNumber} style={stripeStyle(i)}>
                                            <td style={textCellStyle}>
                                                <Link onClick={() => onSelect(invoice)}>{invoice.documentNumber}</Link>
                                            </td>
                                            <td style={textCellStyle}>{fmtDate(invoice.invoiceDate)}</td>
                                            <td style={textCellStyle}>{invoice.customerName}</td>
                                            <td style={textCellStyle}>{invoice.ourReference}</td>
                                            <td style={amountStyle(invoice.net)}>{fmtMoney(invoice.net)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div style={{ padding: '0.5rem 0.75rem', color: '#666', fontSize: '0.85rem' }}>
                                {matches.length > shown.length
                                    ? `Visar de ${shown.length} senaste av ${matches.length} träffar. Förfina sökningen för att se fler.`
                                    : `${matches.length} träffar`}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default InvoiceSearch;
